import { useCallback, useSyncExternalStore } from 'react';

/**
 * useSidebarCollapse — Design System v2 [UI-F0].
 *
 * Persiste o estado de colapso da sidebar em `localStorage('tcl-sidebar-
 * collapsed')`. O AppShell (AdminLayout/TenantLayout) aplica o estado:
 * sidebar fica em `--sidebar-w-collapsed` (72px) quando `collapsed=true`,
 * `--sidebar-w` (264px) quando false.
 *
 * [UI-TENANT] Convertido para store compartilhado via useSyncExternalStore.
 * Antes era um useState local por instância — AppShell e Sidebar consumiam
 * o hook separadamente e o toggle do Sidebar só atualizava o seu próprio
 * estado, deixando o AppShell desincronizado (sidebar visualmente colapsava
 * mas o conteúdo não reajustava o margin-left).
 */
const STORAGE_KEY = 'tcl-sidebar-collapsed';

function lerInicial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// Singleton em escopo de módulo + lista de listeners. Toda instância do hook
// se inscreve no mesmo store, então um setCollapsed em qualquer lugar
// notifica todos os useSidebarCollapse() montados.
let estadoAtual = lerInicial();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return estadoAtual;
}

function setCollapsedGlobal(novo: boolean): void {
  if (estadoAtual === novo) return;
  estadoAtual = novo;
  try {
    localStorage.setItem(STORAGE_KEY, novo ? '1' : '0');
  } catch {
    // ignore
  }
  listeners.forEach((l) => l());
}

interface UseSidebarCollapseResult {
  collapsed: boolean;
  toggleCollapsed: () => void;
  setCollapsed: (c: boolean) => void;
}

export function useSidebarCollapse(): UseSidebarCollapseResult {
  const collapsed = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setCollapsed = useCallback((c: boolean) => setCollapsedGlobal(c), []);
  const toggleCollapsed = useCallback(() => setCollapsedGlobal(!estadoAtual), []);

  return { collapsed, toggleCollapsed, setCollapsed };
}
